import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { dxChallengeCopy as c, dxMarketStats, dxEntries, dxLlm, type Bi } from '../../data/digitalExperienceChallenge';
import { FitScale } from './FitScale';
import { EntryComposition, AskBar, AtnaLayer, ENTRY_ICONS } from './challenge/EntryPoints';
import { JourneyShift } from './challenge/JourneyShift';
const EASE = [0.23, 1, 0.32, 1] as const;
export function DigitalChallenge() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <div className="relative max-w-[1680px] mx-auto" data-id="element-3098" data-mp="3qnokyderuace">
      <div className="grid grid-cols-1 xl:grid-cols-[43fr_57fr] gap-12 xl:gap-8 items-start" data-id="element-3099" data-mp="3qog8d0y7t1rl">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="min-w-0" data-id="element-3100" data-mp="3mnedsxf8tdvy">
          <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-3101" data-mp="3mm3la3vmehwf">{pick(c.eyebrow)}</p>
          <h2 className="font-serif text-[2.4rem] sm:text-[3.2rem] 2xl:text-[4rem] leading-[1.04] tracking-tight mt-5" data-id="element-3102" data-mp="3rlyfagaoznlp">
            {c.navy.map(l => <span key={l.fr} className="block text-[#121A3A]" data-id="element-3103" data-mp="3dn8cck16ytpf">{pick(l)}</span>)}
            {c.indigo.map(l => <span key={l.fr} className="block text-[#4F46E5]" data-id="element-3104" data-mp="3hok8l8ekmd6l">{pick(l)}</span>)}
          </h2>
          <p className="font-sans text-[17px] md:text-[19px] text-[#4A5268] leading-relaxed mt-7 max-w-[640px]" data-id="element-3105" data-mp="990gicpmz0t4">{pick(c.body)}</p>

          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0 sm:divide-x sm:divide-[#E4E6F0]" data-id="element-3106" data-mp="evo3ouuthbh">
            {dxMarketStats.map((s, i) => <li key={s.source.fr} className={`flex flex-col ${i === 0 ? 'sm:pr-6' : 'sm:px-6'}`} data-id="element-3107" data-mp="4ee88ieupod9" data-mpi={i}>
                <span className="font-serif text-[3rem] md:text-[3.4rem] leading-none text-[#4F46E5]" data-id="element-3108" data-mp="s4i586xjapva">{pick(s.value)}</span>
                <span className="mt-4 font-sans text-[15px] leading-snug text-[#4A5268]" data-id="element-3109" data-mp="16si847hbk8s9">{pick(s.text)}</span>
                <span className="mt-auto pt-5 font-sans text-[13px] text-[#9A9FB0]" data-id="element-3110" data-mp="12r6hpzqmfjj0">{pick(s.source)}</span>
              </li>)}
          </ul>
        </motion.div>

        {/* Multi-entry ecosystem — desktop & tablet */}
        <div className="hidden md:block min-w-0" data-id="element-3111" data-mp="sro19t9x6fvp">
          <FitScale width={1000} height={700} data-id="element-3112" data-mp="sv3ip2r2g10a"><EntryComposition data-id="element-3113" data-mp="ntw6mrhw4fnt" /></FitScale>
        </div>

        {/* Mobile — vertical sequence converging on ATNA */}
        <div className="md:hidden" data-id="element-3114" data-mp="j24faeqwu5wm">
          <FitScale width={400} height={60} data-id="element-3115" data-mp="j132o5u8xbcs"><AskBar width={400} data-id="element-3116" data-mp="f0szf0xu8z1m" /></FitScale>
          <ol className="relative mt-6 space-y-3 pl-6 border-l border-[#C9C5F5] ml-3" data-id="element-3117" data-mp="f1uctc2btxmi">
            {dxEntries.map(e => {
            const Icon = ENTRY_ICONS[e.key];
            return <li key={e.key} className="relative rounded-2xl bg-white border border-[#EEF0F4] p-4 flex items-start gap-3" data-id="element-3118" data-mp="52mzgovle2ze">
                  <span className="absolute -left-[31px] top-6 w-3 h-3 rounded-full bg-[#4F46E5] ring-4 ring-[#EEEDFD]" aria-hidden="true" data-id="element-3119" data-mp="6ecipb5l5ho" />
                  <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-3120" data-mp="3r3sx4oggi5yp">
                    <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-3121" data-mp="3w5044larz14b" />
                  </span>
                  <span data-id="element-3122" data-mp="4wm8drto3j1x">
                    <span className="block font-sans text-[14px] font-bold uppercase tracking-wide text-[#4F46E5]" data-id="element-3123" data-mp="33f1dwmkugxy2">{pick(e.label)}</span>
                    <span className="block font-sans text-[14px] text-[#5B6478]" data-id="element-3124" data-mp="38f5sg726ziln">{pick(e.text)}</span>
                    {e.key === 'llm' && <span className="mt-2 inline-block rounded-xl bg-[#6D66F0] px-3 py-2 font-sans text-[13px] text-white" data-id="element-3125" data-mp="3dbcpfacm1jrg">{pick(dxLlm.intent)}</span>}
                  </span>
                </li>;
          })}
          </ol>
          <div className="mt-6" data-id="element-3126" data-mp="2pmbp6adxlex9"><AtnaLayer data-id="element-3127" data-mp="2pls6uskdcoip" /></div>
        </div>
      </div>

      <div className="mt-12 xl:mt-6" data-id="element-3128" data-mp="v6v0i4j2rvbt">
        <JourneyShift data-id="element-3129" data-mp="v7xwgqv98nyl" />
      </div>
    </div>;
}